// Import react libs
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';

// Import style
import { StatisticsWrapper, ChartSection, ChartTitle } from './style';

// Import Global styles
import { Loader } from '../../utils/styles/Atoms';

// Import context
import { AuthContext } from '../../utils/contexts/AuthContext';

// Import components
import { ErrorMessage, ErrorMessageStack } from '../../components/Error';

import LineChart from '../../components/LineChart';
import BarChart from '../../components/BarChart';

// Import hooks
import {
    useGetVisitPerDay,
    useGetViewPerProduct,
    useGetVisitPerLocation,
} from '../../utils/hooks/log';

// Import texts
import statisticsTexts from '../../texts/statistics';
import errorTexts from '../../texts/error';

// Statistics page
function Statistics() {
    // Get the logged user
    const { isAdmin, isLoading: isAuthLoading } = useContext(AuthContext);

    // Get the statistics
    const {
        visitPerDay,
        isLoading: isVisitPerDayLoading,
        isError: isVisitPerDayError,
    } = useGetVisitPerDay();
    const {
        viewPerProduct,
        isLoading: isViewPerProductLoading,
        isError: isViewPerProductError,
    } = useGetViewPerProduct();
    const {
        visitPerLocation,
        isLoading: isVisitPerLocationLoading,
        isError: isVisitPerLocationError,
    } = useGetVisitPerLocation();

    // Filter out locations with unknown labels for the location chart
    const filteredVisitPerLocation = Array.isArray(visitPerLocation)
        ? visitPerLocation.filter(({ location }) => {
              const label = String(location ?? '').trim().toLowerCase();
              return label && label !== 'unknown' && label !== 'unknow';
          })
        : visitPerLocation;

    // If the admin want to add a product
    if (!isAuthLoading && !isAdmin) {
        return <Navigate to="/" />;
    }

    // Agregate isLoading states
    const isLoading =
        isAuthLoading ||
        isVisitPerDayLoading ||
        isViewPerProductLoading ||
        isVisitPerLocationLoading;

    // Agregate isError states
    const isError =
        isVisitPerDayError && isViewPerProductError && isVisitPerLocationError;

    // Return the loader when the datas are loading
    if (isLoading) {
        return (
            <StatisticsWrapper>
                <Loader />
            </StatisticsWrapper>
        );
    }

    // Return an error if the datas failed to be loaded
    if (isError) {
        return (
            <StatisticsWrapper>
                <ErrorMessage
                    critical={true}
                    message={errorTexts.statisticsLoadingError}
                />
            </StatisticsWrapper>
        );
    }

    return (
        <StatisticsWrapper>
            <ErrorMessageStack
                errorMessages={[
                    isVisitPerDayError && {
                        message: errorTexts.failedToLoadVisitPerDayError,
                        critical: false,
                    },
                    isViewPerProductError && {
                        message: errorTexts.failedToLoadViewPerProductError,
                        critical: false,
                    },
                    isVisitPerLocationError && {
                        message: errorTexts.failedToLoadVisitPerLocationError,
                        critical: false,
                    },
                ].filter(Boolean)}
            />
            <ChartSection>
                {!isVisitPerDayError && (
                    <>
                        <ChartTitle>
                            {statisticsTexts.visitPerDayChartTitle}
                        </ChartTitle>
                        <LineChart
                            xAxisKey="day"
                            yAxisKey="visit_count"
                            data={visitPerDay}
                            yAxisHoverLabel={
                                statisticsTexts.visitPerDayChartHoverYAxisName
                            }
                        />
                    </>
                )}
                {!isViewPerProductError && (
                    <>
                        <ChartTitle>
                            {statisticsTexts.viewPerProductChartTitle}
                        </ChartTitle>
                        <BarChart
                            xAxisKey="product_name"
                            yAxisKey="view_count"
                            data={viewPerProduct}
                            yAxisHoverLabel={
                                statisticsTexts.viewPerProductChartHoverYAxisName
                            }
                        />
                    </>
                )}
                {!isVisitPerLocationError && (
                    <>
                        <ChartTitle>
                            {statisticsTexts.viewPerLocationChartTitle}
                        </ChartTitle>
                        <BarChart
                            xAxisKey="location"
                            yAxisKey="visit_count"
                            data={filteredVisitPerLocation}
                            yAxisHoverLabel={
                                statisticsTexts.viewPerLocationChartHoverYAxisName
                            }
                        />
                    </>
                )}
            </ChartSection>
        </StatisticsWrapper>
    );
}

export default Statistics;
